import React, { useState, useEffect } from 'react';
import "../components/table.css";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  SportsSoccer,
  LocationOn,
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { courtsAPI, sportsAPI } from '../services/api';

const CourtSchema = Yup.object().shape({
  label: Yup.string().required('Court label is required'),
  sport_id: Yup.number().required('Sport is required'),
  is_active: Yup.boolean(),
});

const Courts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [courts, setCourts] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourt, setEditingCourt] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, court: null });
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchCourts();
    fetchSports();
  }, []);

  const fetchCourts = async () => {
    try {
      setLoading(true);
      const response = await courtsAPI.getAll();
      setCourts(response.data.data);
    } catch (error) {
      console.error('Error fetching courts:', error);
      setAlert({ type: 'error', message: 'Failed to load courts' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSports = async () => {
    try {
      const response = await sportsAPI.getAll();
      setSports(response.data.data);
    } catch (error) {
      console.error('Error fetching sports:', error);
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    try {
      if (editingCourt) {
        await courtsAPI.update(editingCourt.id, values);
        setAlert({ type: 'success', message: 'Court updated successfully' });
      } else {
        await courtsAPI.create(values);
        setAlert({ type: 'success', message: 'Court created successfully' });
      }

      fetchCourts();
      setDialogOpen(false);
      setEditingCourt(null);
      resetForm();
    } catch (error) {
      console.error('Error saving court:', error);
      setAlert({ type: 'error', message: 'Failed to save court' });
    }
  };

  const handleDelete = async () => {
    try {
      await courtsAPI.delete(deleteDialog.court.id);
      setAlert({ type: 'success', message: 'Court deleted successfully' });
      fetchCourts();
      setDeleteDialog({ open: false, court: null });
    } catch (error) {
      console.error('Error deleting court:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete court';
      setAlert({ type: 'error', message: errorMessage });
    }
  };

  const openEditDialog = (court) => {
    setEditingCourt(court);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingCourt(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingCourt(null);
  };

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreateDialog}
        >
          Add Court
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <TableContainer className="app-table-responsive">
          <Table className="app-table app-table-bordered app-table-light">
            <TableHead className="app-table-light">
              <TableRow>
                <TableCell>Court Label</TableCell>
                <TableCell>Sport</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Loading courts...
                  </TableCell>
                </TableRow>
              ) : courts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No courts found
                  </TableCell>
                </TableRow>
              ) : (
                courts.map((court) => (
                  <TableRow key={court.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationOn color="primary" />
                        {court.court_label}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SportsSoccer color="secondary" />
                        {court.sport_name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={court.is_active ? 'Active' : 'Inactive'}
                        color={court.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(court)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteDialog({ open: true, court })}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Court Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingCourt ? 'Edit Court' : 'Add New Court'}
        </DialogTitle>
        <Formik
          initialValues={{
            label: editingCourt?.label || '',
            sport_id: editingCourt?.sport_id || '',
            is_active: editingCourt?.is_active ?? true,
          }}
          validationSchema={CourtSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, handleChange }) => (
            <Form>
              <DialogContent>
                <Field
                  as={TextField}
                  margin="dense"
                  label="Court Label"
                  name="label"
                  fullWidth
                  error={touched.label && !!errors.label}
                  helperText={touched.label && errors.label}
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Sport</InputLabel>
                  <Select
                    name="sport_id"
                    value={values.sport_id}
                    onChange={handleChange}
                    label="Sport"
                    error={touched.sport_id && !!errors.sport_id}
                  >
                    {sports.map((sport) => (
                      <MenuItem key={sport.id} value={sport.id}>
                        {sport.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.sport_id && errors.sport_id && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                      {errors.sport_id}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="is_active"
                    value={values.is_active}
                    onChange={handleChange}
                    label="Status"
                  >
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeDialog}>Cancel</Button>
                <Button type="submit" variant="contained">
                  {editingCourt ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, court: null })}
      >
        <DialogTitle>Delete Court</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete court "{deleteDialog.court?.label}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, court: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Courts;